import {
  LeftOutlined,
  LoadingOutlined,
  RightOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import { Button, Form, Input, Modal, Select, Space, Tabs, Tooltip } from "antd";
import { debounce, uniq } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CiRead } from "react-icons/ci";
import { HiRefresh } from "react-icons/hi";
import { IoChatbubbleEllipsesOutline, IoSearchOutline } from "react-icons/io5";
import { MdMailOutline, MdOutlineNewLabel } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import apiFactory from "../../api";
import { GeneralModal } from "../../components/modal/GeneralModal";
import { LabelModal } from "../../components/modal/LabelModal";
import { Directory } from "../../components/select/directory/Directory";
import { DirectoryProvider } from "../../components/select/directory/DirectoryContext";
import { conversationTab, conversationType } from "../../config/Constant";
import { useMenuContext } from "../../context/MenuContext";
import { useLabelStore } from "../../store/LabelStore";
import { useSideBarStore } from "../../store/SideBarStore";
import { useInfoUser } from "../../store/UserStore";
import { openNotificationError } from "../../utils/Utils";
import { ConversationList } from "./ConversationList";
import "./style.scss";

export default function ScrollArrowTabs() {
  const containerRef = useRef(null);

  const items = useMemo(() => {
    return Array.from({ length: 12 }).map((_, i) => ({
      key: String(i),
      label: `Tab ${i + 1}`,
      children: `Content ${i + 1}`,
    }));
  }, []);

  const scrollBy = useCallback((distance) => {
    if (!containerRef.current) return;

    const navWrap = containerRef.current.querySelector(".ant-tabs-nav-wrap");
    if (!navWrap) return;

    navWrap.scrollBy({
      left: distance,
      behavior: "smooth",
    });
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
      }}
    >
      <Button icon={<LeftOutlined />} onClick={() => scrollBy(-200)} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <Tabs items={items} tabBarGutter={16} moreIcon={null} />
      </div>

      <Button icon={<RightOutlined />} onClick={() => scrollBy(200)} />
    </div>
  );
}

const GroupCreating = ({ isModalOpen, setIsModalOpen }) => {
  const [loading, setLoading] = useState(false);
  const { user, languageMap } = useInfoUser();
  const navigate = useNavigate();
  const [data, setData] = useState({
    groupName: "",
    userList: [],
  });
  const [orgList, setOrgList] = useState([]);
  const [form] = Form.useForm();

  const { handleUpdateConversationList, conversationList, activeKey } =
    useMenuContext();

  const cancelModal = () => {
    setOrgList([]);
    setIsModalOpen(false);
    form.resetFields();
  };

  const preventSubmitOnEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const createPersonalChat = async (userIds, type) => {
    setLoading(true);
    const result = await apiFactory.conversationApi.findGroup({
      name: "",
      userIds: userIds,
      conversationType: type,
    });

    if (result?.status !== 200) {
      openNotificationError(result?.message);
      setLoading(false);
    } else {
      setLoading(false);

      if (result?.data === null) {
        const memberId = userIds?.find((id) => id !== user?.userId);
        navigate(`/conversation/virtual/${memberId}`);
        return;
      }

      if (result?.data?.type === conversationType.PERSONAL) {
        const isExist = conversationList?.some(
          (item) => item?.conversationId === result?.data?.conversationId,
        );

        if (!isExist && activeKey === conversationTab.ALL) {
          handleUpdateConversationList([result?.data, ...conversationList]);
        } else {
          const updatedList = [
            result?.data,
            ...conversationList.filter(
              (item) => item?.conversationId !== result?.data?.conversationId,
            ),
          ];

          handleUpdateConversationList(updatedList);
        }
      }

      navigate(`/conversation/chat/${result?.data?.conversationId}`);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    let userIds = orgList
      ?.filter((org) => org?.type === "PERSON" && org?.checked)
      ?.map((org) => org?.objectId);
    userIds?.push(user?.userId);
    userIds = uniq(userIds);

    const type =
      userIds?.length <= 1
        ? conversationType.SELF
        : userIds?.length > 2
          ? conversationType.GROUP
          : conversationType.PERSONAL;

    if ([conversationType.PERSONAL, conversationType.SELF].includes(type)) {
      createPersonalChat(userIds, type);
      cancelModal();
      return;
    }

    const result = await apiFactory.conversationApi.createGroup({
      name: values?.groupName?.trim(),
      userIds: userIds,
      conversationType: type,
    });

    if (result?.status !== 200) {
      openNotificationError(result?.message);
      setLoading(false);
    } else {
      setLoading(false);
      cancelModal();

      if (result?.data?.type === conversationType.PERSONAL) {
        const isExist = conversationList?.some(
          (item) => item?.conversationId === result?.data?.conversationId,
        );

        if (
          !isExist &&
          (activeKey === conversationType.PERSONAL ||
            activeKey === conversationType.ALL)
        ) {
          const newConversationListSorted = [result?.data, ...conversationList];

          handleUpdateConversationList(newConversationListSorted);
        } else {
          const updatedList = [
            result?.data,
            ...conversationList.filter(
              (item) => item?.conversationId !== result?.data?.conversationId,
            ),
          ];

          const newConversationListSorted = updatedList;

          handleUpdateConversationList(newConversationListSorted);
        }
      }

      navigate(`/conversation/chat/${result?.data?.conversationId}`);
    }
  };

  return (
    <Modal
      title={
        <div className="text-[#2A55B9] font-bold text-xl">
          {languageMap?.["modal.organization.createGroupName"] ??
            "Create group"}
        </div>
      }
      open={isModalOpen}
      footer={false}
      closeIcon={false}
      onCancel={cancelModal}
      closable
      centered
      width={1200}
    >
      <div className="mb-3 border-b w-[100%]" />
      <Form
        initialValues={data}
        onFinish={onFinish}
        autoComplete="off"
        layout="horizontal"
        form={form}
        onKeyDown={preventSubmitOnEnter}
      >
        <Form.Item name="groupName" className="mb-3">
          <div className="flex items-center gap-[10px] border-2 rounded-md p-2 bg-[#EAEEF0]">
            <Input
              placeholder={
                languageMap?.["modal.organization.nameGroup"] ?? "Group name"
              }
              type="text"
              className="w-[100%]"
              maxLength={70}
            />
          </div>
        </Form.Item>
        <Form.Item name="userList">
          <DirectoryProvider
            orgList={orgList}
            setOrgList={setOrgList}
            isOpen={isModalOpen}
          >
            <Directory />
          </DirectoryProvider>
        </Form.Item>
        <div className="flex justify-center">
          <Button
            className="w-[100px] bg-[#666666]"
            size="large"
            type="primary"
            onClick={cancelModal}
          >
            {languageMap?.["modal.organization.cancel"] ?? "Cancel"}
          </Button>
          <Button
            className="ms-3 w-[100px] bg-[#2A55B9]"
            type="primary"
            size="large"
            htmlType="submit"
            loading={
              loading
                ? {
                    spinning: true,
                    indicator: (
                      <LoadingOutlined style={{ fontSize: 24 }} spin />
                    ),
                  }
                : false
            }
          >
            {languageMap?.["modal.organization.create"] ?? "Create"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

const ConversationTabList = () => {
  const { activeKey, isSearching, onChangeConversationTab } = useMenuContext();
  const { languageMap } = useInfoUser();

  if (isSearching) {
    return (
      <div className="conversation-tab-list">
        <div className="flex flex-col gap-[10px] ml-[10px] mt-[10px]">
          <div className="flex gap-[10px]">
            <Button
              className={`${activeKey === conversationTab.CONTACT_CHAT ? "conversation-tab-selected" : "conversation-tab-unselected"}`}
              onClick={() =>
                onChangeConversationTab(conversationTab.CONTACT_CHAT)
              }
            >
              {languageMap?.["menu.tab.search.contact"] ?? "Contact (Chat)"}
            </Button>
            <Button
              className={`${activeKey === conversationTab.CONTACT_MESSAGE ? "conversation-tab-selected" : "conversation-tab-unselected"}`}
              onClick={() =>
                onChangeConversationTab(conversationTab.CONTACT_MESSAGE)
              }
            >
              {languageMap?.["menu.tab.search.contactMessage"] ??
                "Contact (Message)"}
            </Button>
            <Button
              className={`${activeKey === conversationTab.SEARCH_NOTICE ? "conversation-tab-selected" : "conversation-tab-unselected"}`}
              onClick={() =>
                onChangeConversationTab(conversationTab.SEARCH_NOTICE)
              }
            >
              {languageMap?.["notification.notice"] ?? "Notice"}
            </Button>
          </div>
          <div className="flex gap-[10px]">
            <Button
              className={`${activeKey === conversationTab.SEARCH_CHAT ? "conversation-tab-selected" : "conversation-tab-unselected"}`}
              onClick={() =>
                onChangeConversationTab(conversationTab.SEARCH_CHAT)
              }
            >
              {languageMap?.["menu.tab.search.chat"] ?? "Chat"}
            </Button>
            <Button
              className={`${activeKey === conversationTab.NEW_SEARCH_MESSAGE_RECEIVE ? "conversation-tab-selected" : "conversation-tab-unselected"}`}
              onClick={() =>
                onChangeConversationTab(
                  conversationTab.NEW_SEARCH_MESSAGE_RECEIVE,
                )
              }
            >
              {languageMap?.["menu.tab.search.receive"] ?? "Message (receive)"}
            </Button>
            <Button
              className={`${activeKey === conversationTab.NEW_SEARCH_MESSAGE_SEND ? "conversation-tab-selected" : "conversation-tab-unselected"}`}
              onClick={() =>
                onChangeConversationTab(conversationTab.NEW_SEARCH_MESSAGE_SEND)
              }
            >
              {languageMap?.["menu.tab.search.send"] ?? "Message (send)"}
            </Button>
          </div>
          <div className="flex gap-[10px]">
            <Button
              className={`${activeKey === conversationTab.SEARCH_MESSAGE_RECEIVE ? "conversation-tab-selected" : "conversation-tab-unselected"}`}
              onClick={() =>
                onChangeConversationTab(conversationTab.SEARCH_MESSAGE_RECEIVE)
              }
            >
              {languageMap?.["menu.tab.search.old-receive"] ?? "Message (receive)"}
            </Button>
            <Button
              className={`${activeKey === conversationTab.SEARCH_MESSAGE_SEND ? "conversation-tab-selected" : "conversation-tab-unselected"}`}
              onClick={() =>
                onChangeConversationTab(conversationTab.SEARCH_MESSAGE_SEND)
              }
            >
              {languageMap?.["menu.tab.search.old-send"] ?? "Message (send)"}
            </Button>
          </div>
        </div>
        <div className="border-b-[1px] border-[#e0e0e0] mt-[10px]" />
      </div>
    );
  }

  return (
    <div className="conversation-tab-list">
      <div className="flex flex-col gap-[10px] ml-[10px] mt-[10px]">
        <div className="flex gap-[10px]">
          <Button
            className={`${activeKey === conversationTab.ALL ? "conversation-tab-selected" : "conversation-tab-unselected"}`}
            onClick={() => onChangeConversationTab(conversationTab.ALL)}
          >
            {languageMap?.["menu.tab.all"] ?? "All"}
          </Button>
          <Button
            className={`${activeKey === conversationTab.UNREAD ? "conversation-tab-selected" : "conversation-tab-unselected"}`}
            onClick={() => onChangeConversationTab(conversationTab.UNREAD)}
          >
            {languageMap?.["menu.tab.unread"] ?? "Unread"}
          </Button>
          <Button
            className={`${activeKey === conversationTab.CHAT ? "conversation-tab-selected" : "conversation-tab-unselected"}`}
            onClick={() => onChangeConversationTab(conversationTab.CHAT)}
          >
            {languageMap?.["menu.tab.group"] ?? "Chat"}
          </Button>
          <Button
            className={`${activeKey === conversationTab.NOTICE ? "conversation-tab-selected" : "conversation-tab-unselected"}`}
            onClick={() => onChangeConversationTab(conversationTab.NOTICE)}
          >
            {languageMap?.["notification.notice"] ?? "Notice"}
          </Button>
        </div>
        <div className="flex gap-[10px]">
          <Button
            className={`${activeKey === conversationTab.MESSAGE_RECEIVE ? "conversation-tab-selected" : "conversation-tab-unselected"}`}
            onClick={() =>
              onChangeConversationTab(conversationTab.MESSAGE_RECEIVE)
            }
          >
            {languageMap?.["menu.tab.receive"] ?? "Message (receive)"}
          </Button>
          <Button
            className={`${activeKey === conversationTab.MESSAGE_SEND ? "conversation-tab-selected" : "conversation-tab-unselected"}`}
            onClick={() =>
              onChangeConversationTab(conversationTab.MESSAGE_SEND)
            }
          >
            {languageMap?.["menu.tab.send"] ?? "Message (send)"}
          </Button>
          <Button
            className={`${activeKey === conversationTab.SCHEDULE ? "conversation-tab-selected" : "conversation-tab-unselected"}`}
            onClick={() => onChangeConversationTab(conversationTab.SCHEDULE)}
          >
            {languageMap?.["menu.tab.search.schedule"] ?? "Schedule"}
          </Button>
        </div>
      </div>
      <div className="border-b-[1px] border-[#e0e0e0] mt-[10px]" />
    </div>
  );
};

const ConversationMenu = () => {
  const {
    setSelectedConversation,
    conversationSearch,
    setConversationSearch,
    onSwitchNotification,
    activeKey,
    handleReloadConversation,
    selectedConversation,
    isSearching,
    setSearchConversationList,
    searchConversationRef,
    setConversationSearchText,
    conversationSearchText,
    setSearchMessageConversationList,
    isAsRead,
    setIsAsRead,
    markAsRead,
    showModalLabel,
    labelSelectedId,
    isModalLabelGroup,
    setIsModalLabelGroup,
    setLabelSelectedId,
    handleUpdateConversationList,
    conversationList,
    onUnFocusConversationSearch,
    onFocusConversationSearch,
  } = useMenuContext();

  const { languageMap } = useInfoUser();
  const { labelList, setLabelList } = useLabelStore();
  const [isModalGroup, setIsModalGroup] = useState(false);
  const { isOrganizationSideBar } = useSideBarStore((state) => state);

  /** Ref */
  const debouncedConversationSearch = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 930);

  const filterConversationByLabel = (labelIds) => {
    const updatedConversationSearch = {
      ...conversationSearch,
      labelIds: labelIds,
    };

    setConversationSearch(updatedConversationSearch);
  };

  const getListLabel = async () => {
    const response = await apiFactory.labelApi.getList();
    if (response?.status === 200) {
      const labels = response?.data?.map((s) => {
        return { ...s, value: s?.labelId, label: s?.labelName };
      });
      setLabelList([...labels]);
    } else {
      openNotificationError(response?.message);
    }
  };

  const openCreateGroupModal = async () => {
    setIsModalGroup(true);
  };

  const onStarred = () => {
    setConversationSearch((prevSearch) => ({
      ...prevSearch,
      starred: !prevSearch.starred,
    }));
  };

  const reloadConversation = () => {
    handleReloadConversation();
  };

  const handleResize = () => {
    setIsMobile(window.innerWidth <= 930);
  };

  const onChangeSearch = (e) => {
    setConversationSearchText(e?.target?.value);
    debouncedConversationSearch?.current?.(e?.target?.value);
  };

  const conversationMenuStyle = useMemo(() => {
    if (isOrganizationSideBar) {
      return "detail-chat-menu hidden";
    }

    if (!selectedConversation?.type) {
      return "detail-chat-menu !block";
    }

    return "detail-chat-menu";
  }, [selectedConversation?.type, isOrganizationSideBar]);

  /** Effect */
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    getListLabel();

    debouncedConversationSearch.current = debounce((value) => {
      setConversationSearch({ ...conversationSearch, textSearch: value });

      if (searchConversationRef.current) {
        searchConversationRef.current.scrollTop = 0;
      }
    }, 300);

    /** cleanup when unmount */
    return () => {
      debouncedConversationSearch.current?.cancel?.();
    };
  }, []);

  return (
    <div className={conversationMenuStyle}>
      <div className="header h-[32px]">
        <div className="chat-menu-search">
          <Input
            value={conversationSearchText}
            placeholder={
              languageMap?.["menu.searchConversation.placeholder"] ||
              "Search..."
            }
            onFocus={onFocusConversationSearch}
            onChange={onChangeSearch}
            className="w-full"
            prefix={<IoSearchOutline size={15} />}
            allowClear
          />
        </div>
        {isSearching ? (
          <Button
            className="bg-[#2a56b9] text-white"
            onClick={() => {
              onUnFocusConversationSearch();
              setConversationSearchText("");
              setSearchConversationList([]);
              setSearchMessageConversationList([]);
            }}
          >
            {languageMap?.["search.menu.btn.close"] ?? "Close"}
          </Button>
        ) : (
          <>
            <Tooltip
              placement="bottom"
              title={
                languageMap?.["modal.organization.createGroupName"] ??
                "Create group"
              }
              color={"#0091ff"}
            >
              <Button
                type="default"
                shape="circle"
                className="bg-[#2a56b9]  text-[white]"
                onClick={() => {
                  openCreateGroupModal();
                }}
                icon={<IoChatbubbleEllipsesOutline className="text-[18px]" />}
              />
            </Tooltip>
            <Tooltip
              placement="bottom"
              title={
                languageMap?.["menu.conversation.createMail"] ?? "Create mail"
              }
              color={"#0091ff"}
            >
              <Button
                shape="circle"
                className="bg-[#2a56b9]  text-[white]"
                onClick={onSwitchNotification}
                icon={<MdMailOutline className="text-[18px]" />}
              />
            </Tooltip>
            <Tooltip
              placement="bottom"
              title={languageMap?.["menu.conversation.reload"] ?? "Reload"}
              color={"#0091ff"}
            >
              <Button
                shape="circle"
                className="bg-[#2a56b9]  text-[white]"
                onClick={reloadConversation}
                icon={<HiRefresh className="text-[18px]" />}
              />
            </Tooltip>
          </>
        )}
      </div>

      {!isSearching && (
        <div className="mt-[10px] ml-[10px] flex gap-[8px] items-center pr-3 h-[35px]">
          <Space.Compact className="flex-1">
            <Select
              placeholder={languageMap?.["menu.label.default"] ?? "Label"}
              allowClear
              maxTagPlaceholder={(omittedValues) => (
                <Tooltip
                  title={omittedValues.map(({ label }) => label).join(", ")}
                >
                  <span>Selected +{omittedValues.length}</span>
                </Tooltip>
              )}
              maxTagCount="responsive"
              onChange={filterConversationByLabel}
              options={labelList}
              mode="multiple"
              value={conversationSearch.labelIds}
              className="w-full"
            />
            <Tooltip
              placement="top"
              title={
                languageMap?.["menu.conversation.addLabel"] ??
                "Add or edit labels"
              }
            >
              <Button
                onClick={showModalLabel}
                icon={
                  <span className="flex items-center">
                    <MdOutlineNewLabel
                      className="hover:text-blue-500 text-[#979899]"
                      size={18}
                    />
                  </span>
                }
              />
            </Tooltip>
          </Space.Compact>
          <div className="flex gap-[8px] items-center w-[85px]">
            <Tooltip
              placement="top"
              title={
                languageMap?.["menu.conversation.asRead"] ?? "Mark as read"
              }
            >
              <button className="star" onClick={() => setIsAsRead(true)}>
                <CiRead className="hover:text-blue-500 text-[#979899]" />
              </button>
            </Tooltip>

            <Tooltip
              placement="top"
              title={
                languageMap?.["menu.conversation.important"] ?? "Important"
              }
            >
              <button className="star" onClick={onStarred}>
                {conversationSearch.starred ? (
                  <StarFilled className="star-filled hover:text-blue-500" />
                ) : (
                  <StarOutlined className="hover:text-blue-500 text-[#979899]" />
                )}
              </button>
            </Tooltip>
          </div>
        </div>
      )}
      <div key={activeKey}>
        <ConversationTabList />
      </div>
      <ConversationList />

      {isModalGroup && (
        <GroupCreating
          setSelectedConversation={setSelectedConversation}
          isModalOpen={isModalGroup}
          setIsModalOpen={setIsModalGroup}
        />
      )}

      {isAsRead && (
        <GeneralModal
          title={
            languageMap?.["modal.title.markAll"] ?? "Mark all messages as read"
          }
          content={
            languageMap?.["modal.subTitle.doYouWant"] ??
            "You want to mark all messages as read?"
          }
          onCancel={() => setIsAsRead(false)}
          open={isAsRead}
          onConfirm={markAsRead}
        />
      )}

      {isModalLabelGroup && (
        <LabelModal
          labelSelectedId={labelSelectedId}
          setLabelSelectedId={setLabelSelectedId}
          handleUpdateConversationList={handleUpdateConversationList}
          conversationList={conversationList}
          isModalLabelOpen={isModalLabelGroup}
          setIsModalLabelOpen={setIsModalLabelGroup}
          selectedConversation={selectedConversation}
          setSelectedConversation={setSelectedConversation}
        />
      )}
    </div>
  );
};

export { ConversationMenu };
